import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Home, Menu, X, Mail, Phone, MapPin, Facebook, Linkedin, Instagram, Clock, ArrowRight, ChevronRight } from 'lucide-react';

// --- TikTok SVG ---
const TikTokIcon = () => (
  <svg width="20" height="20" viewBox="0 0 256 256" fill="currentColor" aria-label="TikTok">
    <path d="M164.4 24a44.9 44.9 0 0 0 25.7 8.2V67a78.9 78.9 0 0 1-26.4-4.8v66.6a65.4 65.4 0 1 1-65.4-65.4h.7v33.1a32.3 32.3 0 1 0 22.3 30.6V24h43.1Z"/>
  </svg>
);

// --- CONFIG ---
const CONTACT_EMAIL = 'sales@senrans.com';
const WHATSAPP_NUMBER = '+254757689205';
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}`;
const COMPANY_ADDRESS = 'Repen Complex, Block B, 3rd Floor, Office 310B, Syokimau, off Mombasa Rd, Nairobi, Kenya';
const MAP_EMBED = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.789!2d36.928722!3d-1.378278!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0:0x0!2zMcKwMjInNDEuOCJTIDM2wrA1NSc0My40IkU!5e1!3m2!1sen!2ske!4v1732000000!5m2!1sen!2ske`;
const TIKTOK_LINK = 'https://www.tiktok.com/@senrans_ltd';

const SOCIAL_LINKS = [
  { 
    name: 'Facebook', 
    url: 'https://www.facebook.com/people/Senrans-Ventures/pfbid0XufECfqUEoAKRqbuHT2vkn8EdSvHQxFX8HueCJYLHYb2ajG19v8ntsadvQpXn9nl/',
    icon: Facebook,
    ariaLabel: 'Visit our Facebook page'
  },
  { 
    name: 'Instagram', 
    url: 'https://www.instagram.com/senrans_ltd_',
    icon: Instagram,
    ariaLabel: 'Visit our Instagram profile'
  },
  { 
    name: 'LinkedIn', 
    url: 'https://www.linkedin.com/company/senrans-ltd/',
    icon: Linkedin,
    ariaLabel: 'Visit our LinkedIn company page'
  },
  { 
    name: 'TikTok', 
    url: TIKTOK_LINK,
    icon: TikTokIcon,
    ariaLabel: 'Visit our TikTok profile'
  }
];

// --- NAV ITEM ---
const NavItem = ({ to, children, exact = false }) => {
  const location = useLocation();
  const isActive = exact
    ? location.pathname === to
    : location.pathname === to || location.pathname.startsWith(to + '/');

  return (
    <li>
      <Link
        to={to}
        className={`px-4 py-2 text-white font-medium hover:text-red-400 transition-all duration-200 ${
          isActive ? 'border-b-2 border-red-400' : ''
        }`}
      >
        {children}
      </Link>
    </li>
  );
};

// --- HEADER ---
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  return (
    <header className="bg-black shadow-md sticky top-0 z-50 text-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center" aria-label="Senrans Ltd - Home">
            <img src="/logo.png" alt="Senrans Ltd" className="h-12 w-auto" />
          </Link>

          <nav className="hidden md:block" aria-label="Main navigation">
            <ul className="flex space-x-2">
              <NavItem to="/" exact>Home</NavItem>
              <NavItem to="/about">About Us</NavItem>
              <NavItem to="/products-services">Products & Services</NavItem>
              <NavItem to="/gallery">Gallery</NavItem>
              <NavItem to="/contact">Contact Us</NavItem>
            </ul>
          </nav>

          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)} 
            className="md:hidden text-white p-2"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div id="mobile-menu" className="md:hidden bg-red-800" aria-label="Mobile menu">
          <div className="px-2 pt-1 pb-3 space-y-1">
            <Link to="/" onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 text-white hover:bg-red-700">Home</Link>
            <Link to="/about" onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 text-white hover:bg-red-700">About Us</Link>
            <Link to="/products-services" onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 text-white hover:bg-red-700">Products & Services</Link>
            <Link to="/gallery" onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 text-white hover:bg-red-700">Gallery</Link>
            <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 text-white hover:bg-red-700">Contact Us</Link>
          </div>
        </div>
      )}
    </header>
  );
};

// --- PARTNERS CAROUSEL ---
const PartnersCarousel = () => {
  const partners = [
    { name: "Siemens", logo: "/partners/siemens.png" },
    { name: "Schneider", logo: "/partners/schneider.png" },
    { name: "ABB", logo: "/partners/abb.png" },
    { name: "Danfoss", logo: "/partners/danfoss.png" },
    { name: "Deep Sea Electronics", logo: "/partners/deepsea.png" },
    { name: "Himel", logo: "/partners/himel.png" },
  ];

  const carouselItems = [...partners, ...partners, ...partners];

  return (
    <section className="bg-white py-6">
      <div className="max-w-5xl mx-auto px-2">
        <h2 className="text-xl font-bold text-center mb-6 text-gray-800">PARTNERS WE WORK WITH</h2>
        
        <div className="relative h-28 overflow-hidden">
          <div className="flex items-center h-full animate-carousel-fast">
            {carouselItems.map((p, i) => (
              <div 
                key={`logo-${i}`}
                className="flex-shrink-0 w-44 h-20 mx-3 flex items-center justify-center p-3 bg-gray-50 rounded-lg transition-all duration-300 hover:bg-white hover:shadow-md hover:scale-105 opacity-85 hover:opacity-100"
              >
                <img 
                  src={p.logo} 
                  alt={`${p.name} logo`} 
                  className="max-h-12 max-w-full object-contain transition-all duration-300"
                  style={{ 
                    filter: p.name === "ABB" ? "none" : "inherit"
                  }}
                />
              </div>
            ))}
          </div>
          
          <div className="absolute left-0 top-0 w-16 h-full bg-gradient-to-r from-white via-white/70 to-transparent z-10"></div>
          <div className="absolute right-0 top-0 w-16 h-full bg-gradient-to-l from-white via-white/70 to-transparent z-10"></div>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes carouselFast {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.333%);
          }
        }
        
        .animate-carousel-fast {
          animation: carouselFast 25s linear infinite;
          will-change: transform;
        }
        
        .animate-carousel-fast:hover {
          animation-play-state: paused;
        }
        
        .animate-carousel-fast div:nth-child(3),
        .animate-carousel-fast div:nth-child(9),
        .animate-carousel-fast div:nth-child(15) {
          opacity: 1;
          background-color: white;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          transform: scale(1.05);
        }
      `}</style>
    </section>
  );
};

// --- SEO ---
const SEO = ({ title, description }) => {
  React.useEffect(() => {
    document.title = `${title} | Senrans Ventures Ltd`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = description;
  }, [title, description]);
  return null;
};

// --- CONTENT CARD COMPONENT ---
const ContentCard = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
    {children}
  </div>
);

// --- PRODUCT DETAIL ---
const ProductDetail = ({ title, children, img1, img2 }) => (
  <div className="max-w-6xl mx-auto px-4 py-8">
    <SEO title={title} description="Professional electrical solutions in Kenya" />
    
    <div className="mb-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">{title}</h1>
    </div>

    <div className="grid lg:grid-cols-3 gap-6 mb-8">
      <div className="lg:col-span-2">
        <ContentCard className="p-6">
          <div className="space-y-4">
            {children}
          </div>
        </ContentCard>
      </div>
      
      <div className="space-y-4">
        <ContentCard className="overflow-hidden group">
          <img 
            src={img1} 
            alt={title} 
            loading="lazy" 
            className="w-full h-[500px] object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </ContentCard>
        <ContentCard className="overflow-hidden group">
          <img 
            src={img2} 
            alt={`${title} - additional view`} 
            loading="lazy" 
            className="w-full h-[500px] object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </ContentCard>
      </div>
    </div>
  </div>
);

// --- HOME CONTENT ---
const HomeContent = () => {
  const [current, setCurrent] = React.useState(0);
  const [isHoveredOnCta, setIsHoveredOnCta] = React.useState(false);
  
  const slides = [
    { 
      img: "/images/slide1.jpg", 
      title: "Custom LV Panels", 
      text: "Built to your exact specifications",
      alt: "Custom low voltage electrical panel installation"
    },
    { 
      img: "/products/dse1.jpg", 
      title: "Backup Power Solutions", 
      text: "DSE controllers",
      alt: "Deep Sea Electronics generator controller system"
    },
    { 
      img: "/images/slide3.jpg", 
      title: "Complete Electrical Solutions", 
      text: "Design • Supply • Installation • Commissioning",
      alt: "Electrical installation and maintenance work"
    },
  ];

  React.useEffect(() => {
    if (isHoveredOnCta) return;
    
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [isHoveredOnCta, slides.length]);

  const goToSlide = (index) => {
    setCurrent(index);
  };

  return (
    <div>
      <SEO title="Custom LV Panels & Electrical Services" description="Professional electrical solutions in Nairobi, Kenya" />
      
      {/* HERO SLIDER - FIXED ACCESSIBILITY & PERFORMANCE */}
      <div className="relative h-[calc(100vh-64px)] min-h-[500px] overflow-hidden">
        {slides.map((s, i) => (
          <div key={i} className={`absolute inset-0 transition-opacity duration-1000 ${i === current ? 'opacity-100' : 'opacity-0'}`}>
            <img 
              src={s.img} 
              alt={s.alt}
              className="w-full h-full object-cover"
              fetchpriority={i === 0 ? "high" : "auto"}
              decoding={i === 0 ? "sync" : "async"}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
              <div className="max-w-5xl mx-auto px-4 h-full flex items-center">
                <div className="max-w-2xl">
                  <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 leading-tight">{s.title}</h1>
                  <p className="text-lg md:text-xl text-red-300 mb-6">{s.text}</p>
                  <a 
                    href={WHATSAPP_LINK} 
                    className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105"
                    onMouseEnter={() => setIsHoveredOnCta(true)}
                    onMouseLeave={() => setIsHoveredOnCta(false)}
                    aria-label="Get a quote on WhatsApp"
                  >
                    Get Quote <ArrowRight className="ml-2" size={20} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {/* FIXED: Larger touch targets with proper spacing */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-4" aria-label="Slider controls">
          {slides.map((_, i) => (
            <button 
              key={i} 
              onClick={() => goToSlide(i)} 
              className={`rounded-full transition-all duration-300 flex items-center justify-center ${i === current ? 'bg-white w-10 h-3' : 'bg-gray-400 hover:bg-gray-300 w-6 h-3'}`}
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === current ? "true" : "false"}
            >
              <span className="sr-only">Slide {i + 1}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-2">
        <PartnersCarousel />

        {/* Custom-made LV Panels section */}
        <section className="my-10">
          <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">Custom-made LV Panels</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <ContentCard className="overflow-hidden group">
              <img 
                src="/home/lv-panel.jpg" 
                alt="Custom LV Panel installation" 
                className="w-full h-[280px] object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            </ContentCard>
            <ContentCard className="overflow-hidden group">
              <img 
                src="/home/dse8610.jpg" 
                alt="DSE8610 Controller for generator systems" 
                className="w-full h-[280px] object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            </ContentCard>
          </div>
        </section>

        {/* Combined Electrical Installations & Maintenance Section */}
        <section className="my-10">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Electrical Installations */}
            <div>
              <h2 className="text-xl font-bold mb-4 text-gray-800">Electrical Installations</h2>
              <div className="grid grid-cols-2 gap-3">
                {[1, 2, 3, 4].map((num) => (
                  <ContentCard key={`install${num}`} className="overflow-hidden group h-52">
                    <img 
                      src={`/home/install${num}.jpg`} 
                      alt={`Electrical installation project ${num}`} 
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300 bg-gray-50 p-2"
                      loading={num <= 2 ? "eager" : "lazy"}
                      decoding={num <= 2 ? "sync" : "async"}
                    />
                  </ContentCard>
                ))}
              </div>
            </div>

            {/* Service & Maintenance */}
            <div>
              <h2 className="text-xl font-bold mb-4 text-gray-800">Service & Maintenance</h2>
              <div className="grid grid-cols-2 gap-3">
                {[1, 2, 3, 4].map((num) => (
                  <ContentCard key={`service${num}`} className="overflow-hidden group h-52">
                    <img 
                      src={`/home/service${num}.jpg`} 
                      alt={`Electrical maintenance service ${num}`} 
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300 bg-gray-50 p-2"
                      loading="lazy"
                      decoding="async"
                    />
                  </ContentCard>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6 rounded-xl my-10">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-xl md:text-2xl font-bold mb-3">Ready to Power Your Project?</h3>
            <p className="text-red-100 mb-5 text-sm">Get professional electrical solutions tailored to your specific requirements.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a 
                href={WHATSAPP_LINK} 
                className="bg-white text-red-600 hover:bg-gray-100 px-5 py-2.5 rounded-lg font-bold text-base inline-flex items-center justify-center transition-all duration-300 hover:scale-105"
                onMouseEnter={() => setIsHoveredOnCta(true)}
                onMouseLeave={() => setIsHoveredOnCta(false)}
                aria-label="Get instant quote on WhatsApp"
              >
                <Phone className="mr-2" size={20} /> Get Instant Quote
              </a>
              <Link 
                to="/contact" 
                className="border border-white text-white hover:bg-white/10 px-5 py-2.5 rounded-lg font-bold text-base inline-flex items-center justify-center transition-all duration-300"
                aria-label="Schedule a consultation"
              >
                Schedule Consultation
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

// --- ABOUT US ---
const About = () => (
  <div className="max-w-6xl mx-auto px-4 py-8">
    <SEO title="About Senrans Ltd" description="Electrical engineering company in Kenya" />
    
    <div className="mb-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">About Senrans Ventures Ltd</h1>
    </div>

    <div className="grid lg:grid-cols-2 gap-8 mb-8">
      <ContentCard className="p-6">
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            Senrans Ltd is an electrical engineering company that offers comprehensive Low Voltage & Medium Voltage solutions from design, supply, installation, commissioning to after-sales support. We are at the forefront of promoting clean energy and power backup solutions.
          </p>
          <p className="text-gray-700 leading-relaxed">
            We specialize in assembling custom LV panels for all applications, ensuring each solution meets the highest standards of quality, safety, and reliability.
          </p>
          
          <div className="bg-red-50 p-4 rounded-lg">
            <h4 className="font-bold text-gray-900 mb-3">Our Panel Range Includes:</h4>
            <ul className="grid grid-cols-1 gap-2">
              <li className="flex items-center"><ChevronRight size={16} className="text-red-600 mr-2" /> Changeover panels</li>
              <li className="flex items-center"><ChevronRight size={16} className="text-red-600 mr-2" /> Synch & Control panels</li>
              <li className="flex items-center"><ChevronRight size={16} className="text-red-600 mr-2" /> Metering panels</li>
              <li className="flex items-center"><ChevronRight size={16} className="text-red-600 mr-2" /> Distribution boards</li>
              <li className="flex items-center"><ChevronRight size={16} className="text-red-600 mr-2" /> Power factor correction</li>
              <li className="flex items-center"><ChevronRight size={16} className="text-red-600 mr-2" /> Motor control centers</li>
            </ul>
          </div>
        </div>
      </ContentCard>

      <div className="space-y-6">
        <ContentCard className="overflow-hidden group">
          <img 
            src="/images/project-person1.jpg" 
            alt="Senrans team working on electrical project" 
            className="w-full h-[450px] object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </ContentCard>
        <ContentCard className="overflow-hidden group">
          <img 
            src="/images/project-person2.jpg" 
            alt="Professional electrical installation in progress" 
            className="w-full h-[450px] object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </ContentCard>
      </div>
    </div>
  </div>
);

// --- PRODUCTS LIST ---
const ProductsList = () => {
  const categories = [
    { title: "MV Solutions", img: "/products/mv.jpg", to: "/products-services/mv", desc: "11kV-33kV switchgear and substation solutions" },
    { title: "LV Panels", img: "/products/lv.jpg", to: "/products-services/lv", desc: "Custom low voltage panels for all applications" },
    { title: "DSE Controllers", img: "/products/dse.jpg", to: "/products-services/dse", desc: "Generator control and monitoring systems" },
    { title: "Circuit Breakers", img: "/products/breaker.jpg", to: "/products-services/breaker", desc: "LV protection devices and accessories" },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <SEO title="Products & Services" description="Electrical products and services in Kenya" />
      
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">LV & MV Products & Services</h1>
      </div>

      <ContentCard className="p-6 mb-8 bg-red-50">
        <h3 className="text-xl font-bold text-gray-900 mb-4">What We Do:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            "Electrical installations & maintenance",
            "Power factor correction systems",
            "Annual Maintenance Contracts (AMCs)",
            "Electrical inspections & audits",
            "Thermography services",
            "LV panel assembly & installation",
            "Supply of LV/MV switchgear",
            "DSE generator controllers",
            "Backup power solutions"
          ].map((service, index) => (
            <div key={index} className="flex items-start hover:text-red-600 transition-colors">
              <ChevronRight size={16} className="text-red-600 mt-1 mr-2 flex-shrink-0" />
              <span className="text-gray-800 hover:text-red-600">{service}</span>
            </div>
          ))}
        </div>
      </ContentCard>

      <div className="grid md:grid-cols-2 gap-6">
        {categories.map((cat, i) => (
          <Link to={cat.to} key={i} className="block relative group" aria-label={`View ${cat.title} details`}>
            <div className="relative overflow-hidden rounded-lg">
              <img 
                src={cat.img} 
                alt={cat.desc} 
                className="w-full h-[300px] object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent flex flex-col justify-end p-6">
                <h3 className="text-2xl font-bold text-white mb-2">{cat.title}</h3>
                <p className="text-gray-200 mb-3">{cat.desc}</p>
                <div className="flex items-center text-white font-medium">
                  View Details <ArrowRight size={20} className="ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

// --- MV PAGE ---
const MV = () => (
  <ProductDetail 
    title="Medium Voltage (MV) Switchgear & Solutions" 
    img1="/products/mv1.jpg" 
    img2="/products/mv2.jpg"
  >
    <div className="space-y-4">
      <p className="text-gray-700 leading-relaxed">
        We design, supply, and install <strong className="text-red-600">IEC-compliant Medium Voltage (11kV – 33kV)</strong> switchgear for industrial, commercial, and utility applications. Our MV solutions ensure reliable power distribution with minimal downtime.
      </p>
      
      <div>
        <h4 className="text-xl font-bold text-gray-900 mb-3">Key Features:</h4>
        <ul className="space-y-2">
          {[
            "Air-insulated (AIS) & Gas-insulated (GIS) switchgear",
            "Vacuum or SF6 circuit breakers",
            "Protection relays with IEC 61850 communication",
            "Compact modular design for space efficiency",
            "Remote monitoring & SCADA integration"
          ].map((feature, index) => (
            <li key={index} className="flex items-start hover:text-red-600 transition-colors">
              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                <ChevronRight size={14} className="text-red-600" />
              </div>
              <span className="text-gray-700 hover:text-red-600">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <p><strong className="text-red-600">Applications:</strong> Substations, factories, hospitals, data centers, solar farms.</p>
    </div>
  </ProductDetail>
);

// --- LV PAGE ---
const LV = () => (
  <ProductDetail 
    title="Custom Low Voltage (LV) Panels – Built to Your Specs" 
    img1="/products/lv1.jpg" 
    img2="/products/lv2.jpg"
  >
    <div className="space-y-4">
      <p className="text-gray-700 leading-relaxed">
        Senrans specializes in <strong className="text-red-600">fully customized LV panels</strong> engineered for performance, safety, and scalability. From distribution boards to complex control systems — we build it in-house.
      </p>
      
      <div>
        <h4 className="text-xl font-bold text-gray-900 mb-3">Our Range Includes:</h4>
        <ul className="space-y-2">
          {[
            "Changeover panels",
            "Synch & Control panels",
            "Metering panels",
            "Main & Sub-distribution boards",
            "Power factor correction banks"
          ].map((item, index) => (
            <li key={index} className="flex items-start hover:text-red-600 transition-colors">
              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                <ChevronRight size={14} className="text-red-600" />
              </div>
              <span className="text-gray-700 hover:text-red-600">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <p><strong className="text-red-600">Standards:</strong> IEC 61439 | Form 2–4 segregation | Digital metering | Modbus-ready</p>
      <p><strong className="text-red-600">Applications:</strong> Factories, offices, schools, hotels, residential complexes.</p>
    </div>
  </ProductDetail>
);

// --- DSE PAGE ---
const DSE = () => (
  <ProductDetail 
    title="Deep Sea Electronics (DSE) Generator Controllers" 
    img1="/products/dse1.jpg" 
    img2="/products/dse2.jpg"
  >
    <div className="space-y-4">
      <p className="text-gray-700 leading-relaxed">
        We stock the entire range of original DSE generator controllers to suit different applications. Our DSE solutions are backed by a strong technical team that offers full support — from product selection, to installation & configuration.
      </p>

      <div>
        <h4 className="text-xl font-bold text-gray-900 mb-3">Models Available:</h4>
        
        <div className="space-y-4">
          <div className="hover:text-red-600 transition-colors">
            <strong className="text-lg">Manual & Auto Start Controllers</strong>
            <p className="text-gray-700 ml-4 hover:text-red-600">DSE3110MKII, DSE6110MKIII, DSE7310MKII, DSE7410MKII</p>
          </div>

          <div className="hover:text-red-600 transition-colors">
            <strong className="text-lg">Auto Main Failure (AMF) Controllers</strong>
            <p className="text-gray-700 ml-4 hover:text-red-600">DSE4520MKII, DSE6020MKII, DSE6120MKIII, DSE7320MKII, DSE7420MKII</p>
          </div>

          <div className="hover:text-red-600 transition-colors">
            <strong className="text-lg">Synch & Load Sharing Controllers</strong>
            <p className="text-gray-700 ml-4 hover:text-red-600">DSE8610MKII, DSE8620MKII, DSE8660MKII</p>
          </div>

          <div className="hover:text-red-600 transition-colors">
            <strong className="text-lg">Auto Transfer Switch Controllers</strong>
            <p className="text-gray-700 ml-4 hover:text-red-600">DSE327, DSE331, DSE334, DSE335</p>
          </div>

          <div className="hover:text-red-600 transition-colors">
            <strong className="text-lg">Trickle & Intelligent Chargers (12V & 24V)</strong>
          </div>

          <div className="hover:text-red-600 transition-colors">
            <strong className="text-lg">4G Gateways & Antenna</strong>
            <p className="text-gray-700 ml-4 hover:text-red-600">DSE890 MKII 4G Gateway, DSE891 Ethernet Gateway</p>
          </div>

          <div className="hover:text-red-600 transition-colors">
            <strong className="text-lg">Communication Interface Modules</strong>
            <p className="text-gray-700 ml-4 hover:text-red-600">DSE855, DSE857, DSE815</p>
          </div>
        </div>
      </div>

      <p><strong className="text-red-600">Applications:</strong> Hospitals, banks, telcos, data centers, industrial plants, commercial buildings.</p>
    </div>
  </ProductDetail>
);

// --- BREAKER PAGE ---
const Breaker = () => (
  <ProductDetail 
    title="Low Voltage Circuit Breakers (MCCB & ACB)" 
    img1="/products/breaker1.jpg" 
    img2="/products/breaker2.jpg"
  >
    <div className="space-y-4">
      <p className="text-gray-700 leading-relaxed">
        We supply and install a comprehensive range of Low Voltage circuit breakers from leading manufacturers, ensuring optimal protection for your electrical systems. Our solutions cover MCBs, MCCBs, and ACBs for all protection needs.
      </p>

      <div>
        <h4 className="text-xl font-bold text-gray-900 mb-3">Product Range:</h4>
        <div className="grid md:grid-cols-3 gap-3">
          <div className="bg-red-50 p-4 rounded-lg hover:bg-red-100 transition-colors">
            <h5 className="font-bold text-red-600">MCBs</h5>
            <p className="text-gray-600">Miniature Circuit Breakers (6A-125A)</p>
          </div>
          <div className="bg-red-50 p-4 rounded-lg hover:bg-red-100 transition-colors">
            <h5 className="font-bold text-red-600">MCCBs</h5>
            <p className="text-gray-600">Molded Case Circuit Breakers (125A-3200A)</p>
          </div>
          <div className="bg-red-50 p-4 rounded-lg hover:bg-red-100 transition-colors">
            <h5 className="font-bold text-red-600">ACBs</h5>
            <p className="text-gray-600">Air Circuit Breakers (630A-6300A)</p>
          </div>
        </div>
      </div>

      <p><strong className="text-red-600">Applications:</strong> Panel building, motor protection, generator protection, main distribution boards.</p>
    </div>
  </ProductDetail>
);

// --- GALLERY ---
const Gallery = () => {
  const images = Array.from({ length: 12 }, (_, i) => `/gallery/new${i + 1}.jpg`);
  
  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <SEO title="Project Gallery" description="View our completed electrical projects" />
      
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Gallery</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {images.map((src, i) => (
          <ContentCard key={i} className="overflow-hidden group">
            <img 
              src={src} 
              alt={`Electrical project ${i+1}`} 
              loading="lazy"
              className="w-full h-[300px] object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </ContentCard>
        ))}
      </div>
    </div>
  );
};

// --- CONTACT ---
const Contact = () => (
  <div className="max-w-6xl mx-auto px-4 py-8">
    <SEO title="Contact Us" description="Get in touch for electrical solutions in Kenya" />
    
    <div className="mb-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Contact Us</h1>
    </div>

    <div className="grid lg:grid-cols-2 gap-6">
      <ContentCard className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Contact Information</h3>
        <div className="space-y-5">
          <div className="flex items-start hover:text-red-600 transition-colors">
            <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center mr-4 flex-shrink-0">
              <Phone size={20} className="text-red-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Phone Numbers</p>
              <a href={WHATSAPP_LINK} className="text-gray-600 hover:text-red-600 block" aria-label="Call us at +254 757 689 205">+254 757 689 205</a>
              <a href="https://wa.me/254755388676" className="text-gray-600 hover:text-red-600 block" aria-label="Call us at +254 755 388 676">+254 755 388 676</a>
            </div>
          </div>
          <div className="flex items-start hover:text-red-600 transition-colors">
            <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center mr-4 flex-shrink-0">
              <Mail size={20} className="text-red-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Email</p>
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-gray-600 hover:text-red-600 block" aria-label="Email us at sales@senrans.com">{CONTACT_EMAIL}</a>
            </div>
          </div>
          <div className="flex items-start hover:text-red-600 transition-colors">
            <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center mr-4 flex-shrink-0">
              <MapPin size={20} className="text-red-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Location</p>
              <p className="text-gray-600 hover:text-red-600">{COMPANY_ADDRESS}</p>
            </div>
          </div>
          <div className="flex items-start hover:text-red-600 transition-colors">
            <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center mr-4 flex-shrink-0">
              <Clock size={20} className="text-red-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Business Hours</p>
              <p className="text-gray-600 hover:text-red-600">Mon-Fri: 8:00 AM – 5:00 PM</p>
              <p className="text-gray-600 hover:text-red-600">Saturday: 8:00 AM – 12:00 PM</p>
            </div>
          </div>
        </div>
      </ContentCard>

      <ContentCard className="overflow-hidden">
        <iframe 
          title="Senrans Ltd Location Map - Repen Complex, Syokimau, Nairobi" 
          src={MAP_EMBED} 
          width="100%" 
          height="400" 
          style={{ border: 0 }} 
          allowFullScreen 
          loading="lazy"
          aria-label="Interactive map showing Senrans Ltd location"
        ></iframe>
      </ContentCard>
    </div>
  </div>
);

// --- FOOTER ---
const Footer = () => (
  <footer className="bg-black text-white mt-8">
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pb-6 border-b border-gray-800">
        <div>
          <Link to="/" className="flex items-center mb-3" aria-label="Senrans Ltd - Home">
            <img src="/logo.png" alt="Senrans Ltd logo" className="h-12 w-auto" />
          </Link>
          <p className="text-gray-400 text-xs mb-4">
            <strong>Senrans Ventures Ltd</strong> — Professional electrical engineering solutions in Kenya.
          </p>
          <div className="flex space-x-3 mt-2">
            {SOCIAL_LINKS.map((social, index) => {
              const Icon = social.icon;
              return (
                <a 
                  key={index}
                  href={social.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label={social.ariaLabel}
                >
                  <Icon size={18} />
                </a>
              );
            })}
          </div>
        </div>

        <div>
          <h3 className="font-bold mb-3 text-sm pb-2 border-b border-red-600">Quick Links</h3>
          <ul className="text-xs space-y-2 pt-2">
            <li><Link to="/" className="text-gray-400 hover:text-red-400" aria-label="Go to Home page">Home</Link></li>
            <li><Link to="/about" className="text-gray-400 hover:text-red-400" aria-label="Go to About Us page">About Us</Link></li>
            <li><Link to="/products-services" className="text-gray-400 hover:text-red-400" aria-label="Go to Products & Services page">Products & Services</Link></li>
            <li><Link to="/gallery" className="text-gray-400 hover:text-red-400" aria-label="Go to Gallery page">Gallery</Link></li>
            <li><Link to="/contact" className="text-gray-400 hover:text-red-400" aria-label="Go to Contact Us page">Contact Us</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="font-bold mb-3 text-sm pb-2 border-b border-red-600">Products</h3>
          <ul className="text-xs space-y-2 pt-2">
            <li><Link to="/products-services/mv" className="text-gray-400 hover:text-red-400" aria-label="View MV Solutions">MV Solutions</Link></li>
            <li><Link to="/products-services/lv" className="text-gray-400 hover:text-red-400" aria-label="View LV Panels">LV Panels</Link></li>
            <li><Link to="/products-services/dse" className="text-gray-400 hover:text-red-400" aria-label="View DSE Controllers">DSE Controllers</Link></li>
            <li><Link to="/products-services/breaker" className="text-gray-400 hover:text-red-400" aria-label="View Circuit Breakers">Circuit Breakers</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="font-bold mb-3 text-sm pb-2 border-b border-red-600">Contact</h3>
          <div className="space-y-2 pt-2">
            <p className="text-gray-400 text-xs">
              <MapPin size={12} className="inline mr-2" />
              {COMPANY_ADDRESS}
            </p>
            <p className="text-gray-400 text-xs">
              <Phone size={12} className="inline mr-2" />
              <a href={WHATSAPP_LINK} className="hover:text-red-400" aria-label="Call us at +254 757 689 205">+254 757 689 205</a>
            </p>
            <p className="text-gray-400 text-xs">
              <Mail size={12} className="inline mr-2" />
              <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-red-400" aria-label="Email us at sales@senrans.com">{CONTACT_EMAIL}</a>
            </p>
          </div>
        </div>
      </div>
      
      <div className="pt-4 text-center">
        <p className="text-gray-500 text-xs">
          © {new Date().getFullYear()} Senrans Ventures Ltd. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// --- SCROLL TO TOP ---
const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => window.scrollTo(0, 0), [pathname]);
  return null;
};

// --- 404 PAGE ---
const NotFound = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-6xl font-bold text-red-600 mb-4">404</h1>
      <p className="text-2xl mb-6">Page not found</p>
      <Link to="/" className="text-red-600 underline text-lg">Back to Home</Link>
    </div>
  </div>
);

// --- APP ---
const App = () => (
  <Router>
    <ScrollToTop />
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<HomeContent />} />
          <Route path="/about" element={<About />} />
          <Route path="/products-services" element={<ProductsList />} />
          <Route path="/products-services/mv" element={<MV />} />
          <Route path="/products-services/lv" element={<LV />} />
          <Route path="/products-services/dse" element={<DSE />} />
          <Route path="/products-services/breaker" element={<Breaker />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      
      {/* FIXED: WhatsApp button with proper accessibility */}
      <a 
        href={WHATSAPP_LINK} 
        target="_blank" 
        rel="noopener noreferrer"
        className="fixed bottom-4 right-4 bg-green-500 p-4 rounded-full text-white shadow hover:bg-green-600 hover:scale-110 transition-all duration-300 z-50"
        aria-label="Contact us on WhatsApp"
      >
        <Phone size={24} />
        <span className="sr-only">Chat on WhatsApp</span>
      </a>
    </div>
  </Router>
);

export default App;